import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateMatrix } from '../utils/numerology';
import './MatrixVisualization.scss';

interface MatrixVisualizationProps {
  name: string;
  birthDate: string;
}

interface CellData {
  number: number;
  count: number;
  description: string;
  fullText?: string;
}

const MatrixVisualization: React.FC<MatrixVisualizationProps> = ({ name, birthDate }) => {
  const { t, i18n } = useTranslation();
  const [cellData, setCellData] = useState<Map<number, CellData>>(new Map());
  const [hoveredCell, setHoveredCell] = useState<number | null>(null);

  useEffect(() => {
    const result = calculateMatrix(name, birthDate, i18n.language);
    setCellData(result.cellData);
  }, [name, birthDate, i18n.language]);

  const handleCellHover = (number: number) => {
    setHoveredCell(number);
  };

  const handleCellLeave = () => {
    setHoveredCell(null);
  };

  const getCellCount = (number: number): number => {
    return cellData.get(number)?.count || 0;
  };

  return (
    <div className="matrix-visualization">
      <div className="matrix-grid">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => {
          const count = getCellCount(num);
          const isHovered = hoveredCell === num;
          const data = cellData.get(num);

          return (
            <div
              key={num}
              className={`matrix-cell ${count > 0 ? 'matrix-cell--filled' : ''} ${isHovered ? 'matrix-cell--hovered' : ''}`}
              onMouseEnter={() => handleCellHover(num)}
              onMouseLeave={handleCellLeave}
            >
              <div className="matrix-cell__number">{num}</div>
              {count > 0 && (
                <div className="matrix-cell__count">{count}</div>
              )}
              {isHovered && data && (
                <div className="matrix-cell__tooltip">
                  <div className="tooltip-content">
                    <strong>{t('matrix.digit', { number: data.number })}</strong>
                    <p className="tooltip-content__short">{data.description}</p>
                    <p className="tooltip-content__full">{data.fullText || data.description}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MatrixVisualization;
